import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Download, Share2, Twitter, Linkedin, Facebook, Copy, Check, Loader2, Calendar, User, Award } from "lucide-react";
import { toast } from "sonner";
import { API, authFetch } from "@/App";

const CommunityBadge = () => {
  const [badgeData, setBadgeData] = useState(null);
  const [shareLinks, setShareLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadBadgeData();
  }, []);

  const loadBadgeData = async () => {
    try {
      setLoading(true);
      const [badgeRes, shareRes] = await Promise.all([
        authFetch(`${API}/badges/community-badge/preview`),
        authFetch(`${API}/badges/community-badge/share-links`)
      ]);
      
      if (badgeRes.ok && shareRes.ok) {
        const badge = await badgeRes.json();
        const links = await shareRes.json();
        setBadgeData(badge);
        setShareLinks(links);
      }
    } catch (_error) {
      console.error("Error loading badge:", _error);
      toast.error("Failed to load badge");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await authFetch(`${API}/badges/community-badge`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = badgeData?.download_filename || 'mybiz_community_badge.png';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Badge downloaded!");
      }
    } catch (_error) {
      toast.error("Failed to download badge");
    }
  };

  const handleCopyShareText = () => {
    if (badgeData?.share_text) {
      navigator.clipboard.writeText(badgeData.share_text);
      setCopied(true);
      toast.success("Share text copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = (platform) => {
    if (shareLinks?.[platform]) {
      window.open(shareLinks[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#39FF14]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 text-[#39FF14] px-4 py-2 rounded-full text-sm font-medium">
          <Award className="w-4 h-4" />
          Official Community Badge
        </div>
        <h2 className="text-2xl font-bold text-neutral-900">Your Community Badge</h2>
        <p className="text-neutral-600">Share your membership with your network!</p>
      </div>

      {/* Badge Preview - Dark themed to match the badge */}
      <Card className="overflow-hidden bg-black border-[#39FF14]/30">
        <CardContent className="p-6 flex justify-center">
          {badgeData?.image_data && (
            <img 
              src={badgeData.image_data} 
              alt="Community Member Badge" 
              className="w-full max-w-md h-auto rounded-lg shadow-2xl shadow-[#39FF14]/20"
              data-testid="community-badge-image"
            />
          )}
        </CardContent>
      </Card>

      {/* Member Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-neutral-900 to-neutral-800 border-[#39FF14]/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#39FF14]/20 flex items-center justify-center">
                <User className="w-6 h-6 text-[#39FF14]" />
              </div>
              <div>
                <p className="text-sm text-neutral-400">Member Name</p>
                <p className="font-semibold text-lg text-white">{badgeData?.member_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-neutral-900 to-neutral-800 border-[#39FF14]/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#39FF14]/20 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#39FF14]" />
              </div>
              <div>
                <p className="text-sm text-neutral-400">Member Since</p>
                <Badge className="bg-[#39FF14] text-black font-semibold">
                  {badgeData?.member_since}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Download Button */}
      <Button 
        onClick={handleDownload} 
        className="w-full bg-[#39FF14] hover:bg-[#32E010] text-black font-semibold"
        size="lg"
        data-testid="download-badge-btn"
      >
        <Download className="w-5 h-5 mr-2" />
        Download Your Badge
      </Button>

      {/* Share Section */}
      <Card className="border-neutral-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Share2 className="w-5 h-5 text-[#39FF14]" />
            Share on Social Media
          </CardTitle>
          <CardDescription>
            Let your network know you&apos;re part of the My Biz community!
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Social Share Buttons */}
          <div className="grid grid-cols-3 gap-3">
            <Button
              variant="outline"
              onClick={() => handleShare('twitter')}
              className="flex items-center justify-center gap-2 border-sky-200 text-sky-600 hover:bg-sky-50"
              data-testid="share-twitter-btn"
            >
              <Twitter className="w-4 h-4" />
              Twitter
            </Button>
            <Button
              variant="outline"
              onClick={() => handleShare('linkedin')}
              className="flex items-center justify-center gap-2 border-blue-200 text-blue-600 hover:bg-blue-50"
              data-testid="share-linkedin-btn"
            >
              <Linkedin className="w-4 h-4" />
              LinkedIn
            </Button>
            <Button
              variant="outline"
              onClick={() => handleShare('facebook')}
              className="flex items-center justify-center gap-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
              data-testid="share-facebook-btn"
            >
              <Facebook className="w-4 h-4" />
              Facebook
            </Button>
          </div>

          {/* Copy Share Text */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-neutral-700">Share Text</p>
            <div className="bg-neutral-900 p-4 rounded-lg text-sm text-[#39FF14] font-mono">
              &quot;{badgeData?.share_text}&quot;
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyShareText}
              className="w-full border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10"
              data-testid="copy-share-text-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Share Text
                </>
              )}
            </Button>
          </div>

          {/* Pro Tip */}
          <div className="bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-lg p-4">
            <p className="text-sm text-[#006600]">
              <strong>💡 Pro Tip:</strong> Download your badge and upload it directly to your social posts for maximum impact! Use it in your email signature too.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CommunityBadge;
